import React, { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import InfoTooltip from '@components/Inputs/InfoTooltip';
import Label from '@components/Inputs/Label';
import { ISelectOption } from '@constants/entities/ui';
import {
  Button,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Stack,
} from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { CloseIcon } from '@utils/iconsDefs';

export type ControlledRadioGroupProps = Pick<RadioGroupProps, 'row' | 'sx'> & {
  required?: boolean;
  label?: string;
  options: ISelectOption[];
  name: string;
  disabledAll?: boolean;
  triggerFields?: string[];
  handleChange?: (value: string) => boolean | undefined | void;
  withoutRadioMargin?: boolean;
  tooltip?: string;
  fitWidth?: boolean;
  clearable?: boolean;
};

const ControlledRadioGroup: FC<ControlledRadioGroupProps> = ({
  options,
  label,
  required,
  name,
  disabledAll,
  triggerFields = [],
  handleChange,
  withoutRadioMargin,
  tooltip,
  fitWidth = false,
  clearable = false,
  sx,
  ...radioGroupProps
}) => {
  const { control, trigger, clearErrors } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, ...field }, fieldState: { error } }) => {
        const showClear =
          clearable &&
          !disabledAll &&
          field.value != null &&
          field.value !== '';

        const handleClear = () => {
          onChange(null);
          clearErrors(name);
        };

        return (
          <Stack gap={1.3} sx={{ width: fitWidth ? 'initial' : '100%' }}>
            {label && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Label required={required}>
                  {label}
                  {tooltip && <InfoTooltip text={tooltip} />}
                </Label>

                {showClear && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClear}
                    startIcon={
                      <CloseIcon
                        width={6}
                        height={6}
                        fill={themePalette.grey[1000]}
                      />
                    }
                    sx={{
                      minWidth: 'auto',
                      p: 0,
                      alignSelf: 'flex-start',
                      mt: -0.55,
                      fontWeight: 400,
                      fontSize: 13,
                      color: themePalette.grey[1000],
                      '& .MuiButton-startIcon': {
                        ml: 0,
                        mr: '-3px',
                      },
                      '&:hover': {
                        background: 'transparent',
                        color: themePalette.primary.main,
                        '& svg, & svg path': {
                          fill: themePalette.primary.main,
                        },
                      },
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            )}

            <RadioGroup
              {...field}
              onChange={(e) => {
                const preventChange = handleChange?.(e.target.value);

                if (preventChange !== true) onChange(e);

                trigger([name, ...triggerFields]);
              }}
              sx={{
                width: fitWidth ? 'initial' : '100%',
                ...sx,
              }}
              {...radioGroupProps}
            >
              {options.map(({ value, label, disabled }) => (
                <FormControlLabel
                  key={value + label}
                  value={value}
                  label={label}
                  sx={withoutRadioMargin ? { m: 0 } : {}}
                  control={
                    <Radio
                      disabled={disabled || disabledAll}
                      checked={value === field.value}
                    />
                  }
                />
              ))}
            </RadioGroup>

            {!!error && (
              <FormHelperText error={!!error}>{error?.message}</FormHelperText>
            )}
          </Stack>
        );
      }}
    />
  );
};

export default ControlledRadioGroup;
